// The A/B allocation sliders, on the System Admin page.
//
// RESTORED 2026-09-11. #1231 deleted this card when /experiments grew the same
// two-step control for all six kinds, which was correct about the duplication
// and wrong about the timing: operators were still driving the two product
// experiments from here. Both surfaces run for now, and they are two paths onto
// ONE value rather than two values -- this posts the legacy per-experiment dial
// route, the board posts the newer per-kind allocation route, and both land in
// `experiment_weights` in one transaction (recruiter.experiment_dials.set_dial),
// which is the property that makes running both safe. Retire this card once the
// product experiments are actually being driven from /experiments; it is a
// temporary duplicate, not a second console.
//
// It lists the DIAL-DRIVEN experiments (`experiments.dial_driven`, applied by the
// route), which is all three of them as of 2026-09-11: the A/A control used to be
// excluded because its allocation was a literal, and it is now dialled like the
// rest. The predicate stays because the failure it prevents is cheap to bring
// back -- an experiment whose allocation a dial cannot move, offered here, would
// write a row the resolver ignores and then show it as live.
//
// One row per registered experiment: what it is, where the dial sits, and a
// slider to move it. The write is TWO steps (slide, then a pending bar naming the
// exact change, then Apply) and that is kept deliberately: this control moves live
// traffic between arms, and a slider committing on release lets a slipped mouse
// re-dial an experiment with no way to see what it did first. The same page hosts
// the Red Button, which sets the tone -- consequential controls state their effect
// before they have one. What was removed (2026-09-11) is the MANDATORY REASON, not
// the confirmation: typing a sentence was friction, seeing the change before it
// lands is information.
//
// Assignment stays the sticky hash (recruiter/experiments.py): each user's
// 0..99 bucket never moves, so raising the dial only ADDS the low buckets and
// lowering it puts exactly those people back. The copy says so, because an
// operator deciding whether to slide needs to know nobody reshuffles.
//
// Read-only System Admins see the card with the sliders disabled - the dial's
// position is the read half of the area, moving it is the full half - matching
// PlatformControl's canAct/accessKnown contract on the same page.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Badge, Button } from "@recruiter/shared";
import type { AdminExperimentDial } from "@recruiter/shared";

import { setExperimentDial } from "../lib/api";
import { adminKeys, adminExperimentsQuery } from "../lib/queries";
import { AreaBoundary } from "./area-gate";
import { LoadingState, errorMessage } from "./states";
import "./experiment-dials.css";

type Props = {
  /** May this operator actually move a dial? `system_admin` at `full`. */
  canAct: boolean;
  /** Has the grant query answered yet? See PlatformControl for why the
   *  read-only sentence must wait for this while the controls need not. */
  accessKnown: boolean;
};

/** May this move be applied?
 *
 *  ONE condition now: the slider actually moved. The reason requirement is gone
 *  (owner's call, 2026-09-11) along with the route's, and so is the three-character
 *  minimum -- the first three real dial moves wrote `ttt`, `ddddd` and `gggg`, which
 *  is a field people fill with noise rather than an audit trail.
 *
 *  What stays is the no-op check, and it is not friction: a draft equal to the
 *  current value has nothing to apply, and posting it would append a row saying
 *  "0 -> 0" to an append-only trail. Refusing that is arithmetic, not a gate.
 *
 *  Exported and pure so it is testable without a DOM. */
export function canApplyDial(draft: number | null, current: number, reason: string): boolean {
  void reason; // no longer consulted; kept in the signature so callers need no edit
  return draft !== null && draft !== current;
}

export function ExperimentDials({ canAct, accessKnown }: Props) {
  const dials = useQuery(adminExperimentsQuery());

  return (
    <section className="ad-section">
      <h2 className="ad-section__title">A/B experiments</h2>
      <p className="ab-dials__sub">
        How much traffic each experiment gets. The percentage is the share of users in the test
        group; everyone else gets the normal experience. Assignment is sticky per user, so raising a
        dial only adds people and lowering it removes exactly the same ones. Testers pinned through
        the Debug panel are unaffected by the dial.
        {accessKnown && !canAct ? " Your access is read only, so the sliders are disabled." : null}
      </p>
      {/* A LINK, not a second button. This card lists the three dial-driven product
          experiments; putting everything back is a statement about all ninety-six, so
          the control lives on the page whose population that is. Operators drive these
          two sliders from here and would not otherwise find it. */}
      <p className="ab-dials__sub">
        To put every experiment of every kind back to the allocation its code declares,
        which is what a freshly deployed environment runs, use{" "}
        <Link to="/experiments">Restore defaults on the Experiments page</Link>.
      </p>
      {dials.isPending ? <LoadingState label="Reading the dials…" /> : null}
      {dials.isError ? (
        <AreaBoundary
          area="system_admin"
          error={dials.error}
          onRetry={() => void dials.refetch()}
        />
      ) : null}
      {dials.data ? (
        <div className="ab-dials__list">
          {dials.data.experiments.map((experiment) => (
            <DialRow key={experiment.name} experiment={experiment} canAct={canAct} />
          ))}
        </div>
      ) : null}
    </section>
  );
}

function DialRow({ experiment, canAct }: { experiment: AdminExperimentDial; canAct: boolean }) {
  const queryClient = useQueryClient();
  // null = the slider sits where the server says; a number = an unapplied move.
  const [draft, setDraft] = useState<number | null>(null);
  // WHY, and OPTIONAL since 2026-09-11. Still offered, because a reason a human
  // actually wants to leave is the most useful thing in the lifecycle timeline --
  // but never required, because requiring it produced `ttt`, `ddddd` and `gggg` on
  // the first three real moves. An empty reason reads honestly as "nobody said
  // why"; a filler one makes the trail look trustworthy when it is not.
  const [reason, setReason] = useState("");
  const shown = draft ?? experiment.pct;
  const dirty = draft !== null && draft !== experiment.pct;
  const ready = canApplyDial(draft, experiment.pct, reason);

  const apply = useMutation({
    mutationFn: () => setExperimentDial(experiment.name, draft ?? experiment.pct, reason.trim()),
    onSuccess: (fresh) => {
      queryClient.setQueryData(adminKeys.experiments(), fresh);
      setDraft(null);
      setReason("");
    },
  });

  return (
    <div className="ab-dials__row">
      <div className="ab-dials__meta">
        <div className="ab-dials__name">
          <span className="ab-dials__label">{experiment.label}</span>
          <Badge tone={experiment.pct > 0 ? "success" : "neutral"}>
            {experiment.pct > 0 ? `${experiment.pct}% live` : "off (0%)"}
          </Badge>
        </div>
        <p className="ab-dials__blurb">{experiment.blurb}</p>
        <p className="ab-dials__audit">
          {experiment.setBy && experiment.setAt
            ? `Last moved by ${experiment.setBy} on ${new Date(experiment.setAt).toLocaleString()}.`
            : "Never moved; sitting at the code default."}
        </p>
      </div>

      <div className="ab-dials__control">
        <input
          className="ab-dials__slider"
          type="range"
          min={0}
          max={100}
          step={1}
          value={shown}
          disabled={!canAct || apply.isPending}
          aria-label={`${experiment.label} allocation percent`}
          onChange={(event) => setDraft(Number(event.target.value))}
        />
        <span className="ab-dials__value">{shown}%</span>
      </div>

      {dirty ? (
        <div className="ab-dials__pending" role="status">
          <span className="ab-dials__pending-text">
            Move {experiment.label} from {experiment.pct}% to {draft}% of users?
          </span>
          <input
            className="ab-dials__reason"
            type="text"
            value={reason}
            placeholder="Why (optional)"
            aria-label={`Reason for moving ${experiment.label}`}
            disabled={apply.isPending}
            onChange={(event) => setReason(event.target.value)}
          />
          <Button
            size="sm"
            tone="accent"
            disabled={!ready || apply.isPending}
            onClick={() => apply.mutate()}
          >
            {apply.isPending ? "Applying…" : `Apply ${draft}%`}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            tone="neutral"
            disabled={apply.isPending}
            onClick={() => {
              setDraft(null);
              setReason("");
              apply.reset();
            }}
          >
            Discard
          </Button>
        </div>
      ) : null}
      {apply.isError ? <p className="ab-dials__error">{errorMessage(apply.error)}</p> : null}
    </div>
  );
}
